/**
 * MessagingService
 * Phase 1 implementation for retrieving messages based on user role and company
 */

import { supabase } from '@xanectech/supabase-client';
import type {
  Message,
  MessageWithReadStatus,
  MessageRead,
  MessageFilter,
} from '@xanectech/types';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - MessagingService');

export class MessagingService {
  /**
   * Helper method to combine messages with read status
   */
  private static combineMessagesWithReadStatus(
    messages: Message[],
    reads: MessageRead[],
  ): MessageWithReadStatus[] {
    // cater for situation where read is an object (i.e. single read)
    if (reads && !Array.isArray(reads)) {
      reads = [reads];
    }

    return messages.map((message) => {
      const readRecord =
        reads && reads.length > 0
          ? reads.find((read) => read.message_id === message.id)
          : undefined;
      return {
        ...message,
        read: !!readRecord,
        read_at: readRecord?.read_at,
      } as MessageWithReadStatus;
    });
  }

  /**
   * Fetch user-specific messages
   */
  static async getUserMessages(
    userId: string,
  ): Promise<MessageWithReadStatus[]> {
    const { data: messages, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('target_type', 'user')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (messagesError) throw messagesError;

    // Get read status for current user
    const { data: reads, error: readsError } = await supabase
      .from('message_reads')
      .select('*')
      .eq('user_id', userId)
      .eq('target_type', 'user');

    if (readsError) {
      logger.error('Error fetching message reads', { readsError });
      throw readsError;
    }
    return this.combineMessagesWithReadStatus(
      (messages as Message[]) ?? [],
      (reads as MessageRead[]) ?? [],
    );
  }

  /**
   * Fetch company-specific messages
   */
  static async getCompanyMessages(
    userId: string,
    companyId: string,
  ): Promise<MessageWithReadStatus[]> {
    const { data: messages, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('target_type', 'company')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (messagesError) throw messagesError;

    // Get read status for current user
    const { data: reads, error: readsError } = await supabase
      .from('message_reads')
      .select('*')
      .eq('user_id', userId)
      .eq('target_type', 'company');

    if (readsError) throw readsError;

    return this.combineMessagesWithReadStatus(
      (messages as Message[]) ?? [],
      (reads as MessageRead[]) ?? [],
    );
  }

  /**
   * Fetch system-wide messages
   */
  static async getSystemMessages(
    userId: string,
  ): Promise<MessageWithReadStatus[]> {
    const { data: messages, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('target_type', 'system')
      .order('created_at', { ascending: false });

    if (messagesError) throw messagesError;

    // Get read status for current user
    const { data: reads, error: readsError } = await supabase
      .from('message_reads')
      .select('*')
      .eq('user_id', userId)
      .eq('target_type', 'system');

    if (readsError) throw readsError;

    return this.combineMessagesWithReadStatus(
      (messages as Message[]) ?? [],
      (reads as MessageRead[]) ?? [],
    );
  }

  /**
   * Fetch total unread count for a user
   */
  static async getUnreadCount(
    userId: string,
    companyId?: string,
  ): Promise<number> {
    // Fetch all applicable messages using the same methods as getAllMessages
    const [userMessages, companyMessages, systemMessages] = await Promise.all([
      this.getUserMessages(userId),
      companyId
        ? this.getCompanyMessages(userId, companyId)
        : Promise.resolve([]),
      this.getSystemMessages(userId),
    ]);

    // Combine and count unread messages
    const allMessages = [
      ...systemMessages,
      ...companyMessages,
      ...userMessages,
    ];

    return allMessages.filter((message) => !message.read).length;
  }

  /**
   * Orchestrate all three message types and return combined list
   * Note: userRole in filter is reserved for future Phase 2 role-based filtering
   */
  static async getAllMessages(
    filter: MessageFilter,
  ): Promise<MessageWithReadStatus[]> {
    const { userId, companyId } = filter;

    // Fetch all message types in parallel
    const [userMessages, companyMessages, systemMessages] = await Promise.all([
      this.getUserMessages(String(userId)),
      companyId
        ? this.getCompanyMessages(String(userId), String(companyId))
        : Promise.resolve([]),
      this.getSystemMessages(String(userId)),
    ]);

    // Combine all messages
    const allMessages = [
      ...systemMessages,
      ...companyMessages,
      ...userMessages,
    ];

    // Sort by created_at descending
    allMessages.sort(
      (a, b) =>
        new Date(String(b.read_at)).getTime() -
        new Date(String(a.read_at)).getTime(),
    );

    // Apply unread filter if specified
    if (filter.unreadOnly) {
      return allMessages.filter((message) => !message.read);
    }

    return allMessages;
  }
}
